import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@material-ui/core';
import {
  Dashboard as DashboardIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  ShoppingCart as ShoppingCartIcon,
  Work as WorkIcon,
  Timeline as TimelineIcon,
  LocalShipping as LocalShippingIcon,
  Category as CategoryIcon,
  Report as ReportIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Input as InputIcon,
  ViewModule as ViewModuleIcon,
  AccountBox as AccountBoxIcon,
  Notifications as NotificationsIcon,
  ExitToApp as ExitToAppIcon,
  Straighten as StraightenIcon,
  Event as EventIcon,
  Memory as MemoryIcon,
  Category as CategoryIcon2,
  Schedule as ScheduleIcon,
} from '@material-ui/icons';

// Custom icons for specific modules
const CatalogsIcon = () => <CategoryIcon />;
const NCRActionIcon = () => <ReportIcon />;
const StatusIcon = () => <CheckCircleIcon />;
const NCTypeIcon = () => <CategoryIcon />;
const NCSubtypeIcon = () => <CategoryIcon />;
const ClientIcon = () => <BusinessIcon />;
const UOFMIcon = () => <StraightenIcon />;
const EventTypeIcon = () => <EventIcon />;
const MachineIcon = () => <MemoryIcon />;
const OperationTypeIcon = () => <CategoryIcon2 />;
const ShiftsIcon = () => <ScheduleIcon />;

import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  toolbar: theme.mixins.toolbar,
  nested: {
    paddingLeft: theme.spacing(4),
  },
  title: {
    flexGrow: 1,
  },
  userMenu: {
    marginLeft: 'auto',
  },
}));

const Layout: React.FC = ({ children }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Render bare content for login route
  if (location.pathname === '/login') {
    return <>{children}</>;
  }

  const handleMenuClick = (menuKey: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    // Handle logout logic
    handleUserMenuClose();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      subItems: []
    },
    {
      text: 'Parts',
      icon: <CategoryIcon />,
      path: '/parts',
      subItems: []
    },
    {
      text: 'Purchase Orders',
      icon: <ShoppingCartIcon />,
      path: '/purchase-orders',
      subItems: []
    },
    {
      text: 'Work Orders',
      icon: <WorkIcon />,
      path: '/work-orders',
      subItems: []
    },
    {
      text: 'Route Cards',
      icon: <TimelineIcon />,
      path: '/route-cards',
      subItems: []
    },
    {
      text: 'Packing Lists',
      icon: <LocalShippingIcon />,
      path: '/packing-lists',
      subItems: []
    },
    {
      text: 'Products',
      icon: <CategoryIcon2 />,
      path: '/products',
      subItems: []
    },
    {
      text: 'Components',
      icon: <MemoryIcon />,
      path: '/components',
      subItems: []
    },
    {
      text: 'NCR',
      icon: <ReportIcon />,
      path: '/ncr',
      subItems: []
    },
    {
      text: 'Tools & Fixtures',
      icon: <BuildIcon />,
      path: '/tools-fixtures',
      subItems: []
    },
    {
      text: 'Admin',
      icon: <SettingsIcon />,
      path: '/admin',
      subItems: [
        { text: 'Users', icon: <PersonIcon />, path: '/admin/users' },
        { text: 'Roles', icon: <GroupIcon />, path: '/admin/roles' },
      ]
    },
    {
      text: 'Operations',
      icon: <AssignmentIcon />,
      path: '/operations',
      subItems: []
    },
    {
      text: 'Work Instructions',
      icon: <DescriptionIcon />,
      path: '/work-instructions',
      subItems: []
    },
    {
      text: 'Operators',
      icon: <PersonIcon />,
      path: '/operators',
      subItems: []
    },
    {
      text: 'Inputs',
      icon: <InputIcon />,
      path: '/inputs',
      subItems: []
    },
    {
      text: 'Sections',
      icon: <ViewModuleIcon />,
      path: '/sections',
      subItems: []
    },
    {
      text: 'Workflow',
      icon: <TimelineIcon />,
      path: '/workflow',
      subItems: []
    },
    {
      text: 'Catalogs',
      icon: <CatalogsIcon />,
      path: '/catalogs',
      subItems: [
        { text: 'NCR Actions', icon: <NCRActionIcon />, path: '/catalogs/ncraction' },
        { text: 'NC Types', icon: <NCTypeIcon />, path: '/catalogs/nctype' },
        { text: 'NC Subtypes', icon: <NCSubtypeIcon />, path: '/catalogs/ncsubtype' },
        { text: 'Statuses', icon: <StatusIcon />, path: '/catalogs/status' },
        { text: 'Clients', icon: <ClientIcon />, path: '/catalogs/client' },
        { text: 'UOFMs', icon: <UOFMIcon />, path: '/catalogs/uofm' },
        { text: 'Event Types', icon: <EventTypeIcon />, path: '/catalogs/eventtype' },
        { text: 'Machines', icon: <MachineIcon />, path: '/catalogs/machine' },
        { text: 'Operation Types', icon: <OperationTypeIcon />, path: '/catalogs/operationtype' },
        { text: 'Tool Types', icon: <BuildIcon />, path: '/catalogs/tooltype' },
        { text: 'Shifts', icon: <ShiftsIcon />, path: '/catalogs/shifts' },
        { text: 'Part Attributes', icon: <CategoryIcon />, path: '/catalogs/partattributes' },
        { text: 'Part Secondary Attributes', icon: <CategoryIcon />, path: '/catalogs/partsecattributes' },
        { text: 'Part Tertiary Attributes', icon: <CategoryIcon />, path: '/catalogs/parttercattributes' },
        { text: 'Ship To', icon: <LocalShippingIcon />, path: '/catalogs/shipto' },
        { text: 'Part Class', icon: <CategoryIcon />, path: '/catalogs/partclass' },
      ]
    },
  ];

  const drawer = (
    <div>
      <div className={classes.toolbar}>
        <Typography variant="h6" noWrap>
          OPEROUTE
        </Typography>
      </div>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <div key={item.text}>
            <ListItem
              button
              component={RouterLink}
              to={item.subItems.length > 0 ? '#' : item.path}
              selected={isActive(item.path)}
              onClick={item.subItems.length > 0 ? () => handleMenuClick(item.text) : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
              {item.subItems.length > 0 && (
                <ListItemSecondaryAction>
                  {openMenus[item.text] ? <ExpandLess /> : <ExpandMore />}
                </ListItemSecondaryAction>
              )}
            </ListItem>
            {item.subItems.length > 0 && (
              <Collapse in={openMenus[item.text]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItem
                      key={subItem.text}
                      button
                      component={RouterLink}
                      to={subItem.path}
                      selected={isActive(subItem.path)}
                      className={classes.nested}
                    >
                      <ListItemIcon>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </div>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem button component={RouterLink} to="/account">
          <ListItemIcon><AccountCircleIcon /></ListItemIcon>
          <ListItemText primary="Account" />
        </ListItem>
      </List>
    </div>
  );

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            OPEROUTE
          </Typography>
          <div className={classes.userMenu}>
            <IconButton
              color="inherit"
              onClick={handleUserMenuClick}
            >
              <Avatar>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
            >
              <MenuItem onClick={handleUserMenuClose}>
                <AccountBoxIcon style={{ marginRight: 8 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleUserMenuClose}>
                <NotificationsIcon style={{ marginRight: 8 }} />
                Notifications
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ExitToAppIcon style={{ marginRight: 8 }} />
                Logout
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        {drawer}
      </Drawer>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        {children}
      </main>
    </div>
  );
};

export default Layout; 